/**
 * Where a room's boundary is GUESSWORK.
 *
 * A room polygon in this data model is never open: Step 8.8 injects a VIRTUAL
 * edge wherever the scan perimeter has no wall, so the persisted room has a
 * closed ring and simply no room wall on that edge. The edge mapping that would
 * mark it (`edgeRoomWallMapping`) is stripped before persist, so on the client
 * a gap is detectable only geometrically: **a ring edge no room wall covers.**
 *
 * That virtual outline is a guess and carries NO AUTHORITY (user, 2026-09-17).
 * Nothing snaps to it and nothing matches against it — it only tells you WHERE
 * the boundary is unknown. What replaces it is derived from real wall faces.
 *
 * Promoted here from `functions/features/gap-edge-targets.js`, which asks the
 * depth pass the same question and now re-exports these; one implementation, so
 * the LiDAR pass and the user-facing re-close cannot disagree about what counts
 * as a gap.
 *
 * Pure. X-Z plane, `{x, z}` points.
 */

/** A ring edge shorter than this is a trace artefact, not a hole. */
const MIN_GAP_EDGE_M = 0.15;
/** A room wall covers a ring edge when it runs this close to it ... */
const COVER_ACROSS_M = 0.1;
/** ... this close to parallel ... */
const COVER_ANGLE_RAD = 0.05;
/** ... over this share of the edge. */
const COVER_OVERLAP_FRAC = 0.5;
/** A structure wall continues an edge when its line sits within its half thickness plus this ... */
const FLANK_ACROSS_SLACK_M = 0.1;
/** ... within this angle ... */
const FLANK_ANGLE_RAD = 0.09;
/** ... and one of its ends lies within this of the edge. */
const FLANK_END_REACH_M = 0.6;

/** Smallest angle between two headings, modulo π (direction, not sense). */
function angleDiffModPi(a, b) {
  const d = Math.abs(a - b) % Math.PI;
  return Math.min(d, Math.PI - d);
}

/** A wall's two plan endpoints, from its centre, angle and length. */
function wallEnds(wall) {
  const c = wall.center;
  const L = wall.dimensions?.x ?? 0;
  const ry = wall.rotation?.y ?? 0;
  const t = { x: Math.cos(ry), z: -Math.sin(ry) };
  return [
    { x: c.x - (t.x * L) / 2, z: c.z - (t.z * L) / 2 },
    { x: c.x + (t.x * L) / 2, z: c.z + (t.z * L) / 2 },
  ];
}

/** Distance of `p` along the direction `t` measured from `origin`. */
function alongOf(p, origin, t) {
  return (p.x - origin.x) * t.x + (p.z - origin.z) * t.z;
}

/** Unit direction and length of the edge a→b, or null when degenerate. */
function edgeFrame(a, b) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const L = Math.hypot(dx, dz);
  return L > 1e-9 ? { t: { x: dx / L, z: dz / L }, L } : null;
}

/** Is ring edge a→b backed by one of the room's walls? */
function edgeCovered(a, b, roomWalls) {
  const f = edgeFrame(a, b);
  if (!f) return true;
  const edgeRy = Math.atan2(-f.t.z, f.t.x);
  for (const w of roomWalls || []) {
    if (!w?.center || !w.dimensions) continue;
    if (angleDiffModPi(w.rotation?.y ?? 0, edgeRy) > COVER_ANGLE_RAD) continue;
    const across = Math.abs(
      -(w.center.x - a.x) * f.t.z + (w.center.z - a.z) * f.t.x
    );
    if (across > COVER_ACROSS_M) continue;
    const [e0, e1] = wallEnds(w).map((e) => alongOf(e, a, f.t));
    const lo = Math.max(0, Math.min(e0, e1));
    const hi = Math.min(f.L, Math.max(e0, e1));
    if (hi - lo >= COVER_OVERLAP_FRAC * f.L) return true;
  }
  return false;
}

/**
 * The structure wall an uncovered edge CONTINUES: collinear with it, one end
 * within reach. Returns the wall and which end faces the gap.
 *
 * Note what this is and is not. It answers "which surface would a depth pass
 * walk past the end of", which is a question about the GUESSED edge's
 * direction — useful to the LiDAR pass, and deliberately NOT what the
 * user-facing re-close corners onto (that uses the real walls flanking the
 * run, whatever direction they run in).
 */
function flankFor(a, b, structureWalls) {
  const f = edgeFrame(a, b);
  if (!f) return null;
  const edgeRy = Math.atan2(-f.t.z, f.t.x);
  let best = null;
  for (const w of structureWalls || []) {
    if (!w?.center || !w.dimensions) continue;
    if (angleDiffModPi(w.rotation?.y ?? 0, edgeRy) > FLANK_ANGLE_RAD) continue;
    const across = Math.abs(
      -(w.center.x - a.x) * f.t.z + (w.center.z - a.z) * f.t.x
    );
    if (across > (w.dimensions.z ?? 0) / 2 + FLANK_ACROSS_SLACK_M) continue;
    wallEnds(w).forEach((e, k) => {
      const d = Math.min(
        Math.hypot(e.x - a.x, e.z - a.z),
        Math.hypot(e.x - b.x, e.z - b.z)
      );
      if (d <= FLANK_END_REACH_M && (!best || d < best.distanceM)) {
        best = { wall: w, endIndex: k, end: e, distanceM: d };
      }
    });
  }
  return best;
}

/**
 * Consecutive uncovered edges of a ring, as runs of points.
 *
 * A run `[P0, …, Pn]` has n edges; `P0` is where the last real wall left off
 * and `Pn` where the next one picks up, so BOTH ends are real even though
 * everything between them is guesswork. Returns `[]` when the ring is wholly
 * covered — or wholly uncovered, which is not a gap but a room with no walls.
 */
function uncoveredRuns(ring, roomWalls) {
  const n = ring.length;
  const open = ring.map((c, i) => {
    const d = ring[(i + 1) % n];
    return (
      Math.hypot(d.x - c.x, d.z - c.z) >= MIN_GAP_EDGE_M &&
      !edgeCovered(c, d, roomWalls)
    );
  });
  if (open.every(Boolean) || !open.some(Boolean)) return [];
  // start each run just after a covered edge
  let start = open.findIndex((o, i) => !o && open[(i + 1) % n]);
  const runs = [];
  for (let step = 0; step < n; step++) {
    const i = (start + 1 + step) % n;
    if (!open[i]) continue;
    const pts = [ring[i]];
    let j = i;
    while (open[j]) {
      pts.push(ring[(j + 1) % n]);
      j = (j + 1) % n;
      step++;
      if (pts.length > n) break;
    }
    runs.push(pts);
  }
  return runs;
}

/**
 * The same runs, each tagged with the ring INDEX of its first point, so a
 * caller that must rebuild the ring knows which vertices to replace. Index `i`
 * means `ring[i]` is `points[0]`; the run covers edges `i … i + points.length - 2`
 * (modulo the ring length).
 */
function uncoveredRunsIndexed(ring, roomWalls) {
  const key = (p) => `${Math.round(p.x * 1e6)},${Math.round(p.z * 1e6)}`;
  const index = new Map();
  ring.forEach((p, i) => {
    if (!index.has(key(p))) index.set(key(p), i);
  });
  return uncoveredRuns(ring, roomWalls).map((points) => ({
    points,
    startIndex: index.get(key(points[0])) ?? -1,
    edgeCount: points.length - 1,
  }));
}

module.exports = {
  MIN_GAP_EDGE_M,
  COVER_ACROSS_M,
  COVER_ANGLE_RAD,
  COVER_OVERLAP_FRAC,
  FLANK_ACROSS_SLACK_M,
  FLANK_ANGLE_RAD,
  FLANK_END_REACH_M,
  angleDiffModPi,
  wallEnds,
  alongOf,
  edgeFrame,
  edgeCovered,
  flankFor,
  uncoveredRuns,
  uncoveredRunsIndexed,
};
